/**
 * Feed Composer view — S010 production UI.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2348
 *
 * CORRECTION01 accessibility changes (R12):
 * - Character counter: removed aria-live (no per-keystroke announcements)
 *   Counter is connected via aria-describedby, threshold events only
 * - Validation errors: role="alert" only, no redundant aria-live
 * - Error banner: role="status" for polite non-intrusive announcements
 * - No fake status bar rendered (38:2349 is device reference only)
 *
 * CORRECTION01: Focus restoration claim removed (R13).
 */
import type { FeedComposerModel } from './model';
import { isSubmitDisabled, POST_MAX_LENGTH } from './model';
import './FeedComposer.css';

export interface FeedComposerViewProps {
  model: FeedComposerModel;
  onContentChange: (content: string) => void;
  onAnonymousToggle: (anonymous: boolean) => void;
  onPublish: () => void;
  onRetry: () => void;
  onCancel: () => void;
}

/**
 * Primary button label per S010.
 */
const PUBLISH_LABEL = 'Опубликовать';

/**
 * Cancel button label per S010.
 */
const CANCEL_LABEL = 'Отмена';

/**
 * Screen title per S010.
 */
const SCREEN_TITLE = 'Новая запись';

/**
 * Textarea placeholder per S010.
 */
const TEXTAREA_PLACEHOLDER = 'О чем вы думаете?';

/**
 * Anonymous toggle label per S010.
 */
const ANONYMOUS_LABEL = 'Опубликовать анонимно';

/**
 * Counter label template per S010 — "N / 500"
 */
function counterLabel(current: number, max: number): string {
  return `${current} / ${max}`;
}

export function FeedComposerView({
  model,
  onContentChange,
  onAnonymousToggle,
  onPublish,
  onRetry,
  onCancel,
}: FeedComposerViewProps) {
  const { author, content, anonymous, charCount, validationErrors, status, error } = model;
  const submitDisabled = isSubmitDisabled(model);
  const isSubmitting = status === 'submitting';
  const hasError = status === 'error' && error != null;

  // First error message for aria-describedby
  const errorMessage = validationErrors.length > 0
    ? validationMessageForError(validationErrors[0])
    : null;

  return (
    /*
     * Native <form> element for keyboard accessibility.
     * - Enter key in textarea inserts a line break (multiline textarea convention)
     * - Submit is via the Publish button (type="submit") — form invokes onSubmit
     * - All IDs are stable and defined in markup order
     * - Relationships: textarea→(counter,error) via aria-describedby; toggle→label via aria-labelledby
     */
    <form
      className="feed-composer"
      onSubmit={(event) => {
        // CORRECTION02h — direct-boundary probe marker #1.
        // Records whether React's onSubmit was reached and whether
        // the gate (`submitDisabled`) would have prevented onPublish().
        // Console-log only — no behavior change. To be removed
        // once the publish-enter evidence is in.
        // eslint-disable-next-line no-console
        console.log(`[composer-react-submit] submitDisabled=${String(submitDisabled)}`);
        event.preventDefault();
        if (!submitDisabled) {
          // CORRECTION02h — direct-boundary probe marker #2.
          // Records whether the React submit handler actually called
          // onPublish() (i.e. the gate was open). Console-log only —
          // no behavior change. To be removed once the publish-enter
          // evidence is in.
          // eslint-disable-next-line no-console
          console.log('[composer-onpublish-call]');
          onPublish();
        }
      }}
    >
      {/* Top application bar — node 38:2366 */}
      <header className="composer-topbar" role="banner">
        <div className="composer-topbar-inner">
          {/* Cancel control — node 38:2367 */}
          <button
            type="button"
            className="composer-cancel"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="composer-cancel"
          >
            {CANCEL_LABEL}
          </button>

          {/* Screen title — node 38:2369 */}
          <span className="composer-title" data-testid="composer-title">
            {SCREEN_TITLE}
          </span>

          {/* Spacer to balance cancel button */}
          <span className="composer-topbar-spacer" aria-hidden="true" />
        </div>
      </header>

      {/* Main content area */}
      <main className="composer-main" role="main">
        {/* Author block — node 38:2373 */}
        {author && (
          <div className="composer-author" data-testid="composer-author">
            {/* Avatar — node 38:2374 */}
            <div
              className="composer-avatar"
              aria-hidden="true"
              data-testid="composer-avatar"
            >
              {author.initials}
            </div>

            {/* Author info — node 38:2376 */}
            <div className="composer-author-info">
              {/* Name + age — node 38:2377 */}
              <span className="composer-author-name" data-testid="composer-author-name">
                {author.displayName}
                {author.age != null ? `, ${author.age}` : ''}
              </span>
              {/* Location — node 38:2379 */}
              {author.location && (
                <span className="composer-author-location" data-testid="composer-author-location">
                  {author.location}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Divider — node 38:2382 */}
        <div className="composer-divider" role="separator" aria-hidden="true" />

        {/* Text entry region — node 38:2383 */}
        <div className="composer-text-region">
          <textarea
            id="composer-textarea"
            className="composer-textarea"
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            placeholder={TEXTAREA_PLACEHOLDER}
            aria-label={TEXTAREA_PLACEHOLDER}
            aria-describedby="composer-counter composer-error"
            aria-invalid={validationErrors.length > 0}
            disabled={isSubmitting || status === 'success'}
            data-testid="composer-textarea"
            rows={5}
          />
        </div>
      </main>

      {/* Footer — node 38:2386 */}
      <footer className="composer-footer" role="contentinfo">
        <div className="composer-footer-inner">
          {/* Anonymous toggle row — node 38:2387 */}
          <div className="composer-toggle-row">
            {/* Label — node 38:2389 */}
            <span
              className="composer-toggle-label"
              id="composer-toggle-label"
              data-testid="composer-anonymous-label"
            >
              {ANONYMOUS_LABEL}
            </span>

            {/* Toggle switch — node 38:2390 */}
            <button
              type="button"
              role="switch"
              aria-checked={anonymous}
              aria-labelledby="composer-toggle-label"
              className={`composer-toggle${anonymous ? ' composer-toggle--on' : ''}`}
              onClick={() => onAnonymousToggle(!anonymous)}
              disabled={isSubmitting || status === 'success'}
              data-testid="composer-anonymous-toggle"
            >
              <span className="composer-toggle-thumb" aria-hidden="true" />
            </button>
          </div>

          {/* Counter + error row — node 38:2392 */}
          <div className="composer-status-row">
            {/* Character counter — node 38:2393 */}
            {/* CORRECTION01: No aria-live on counter — no per-keystroke announcements (R12) */}
            <span
              id="composer-counter"
              className="composer-counter"
              data-testid="composer-counter"
            >
              {counterLabel(charCount, POST_MAX_LENGTH)}
            </span>

            {/* Validation error — always mounted; role="alert" provides assertive live-region */}
            {/* semantics (aria-live implied). No aria-live="polite" to avoid conflict. */}
            <span
              id="composer-error"
              className="composer-error-message"
              role="alert"
              aria-atomic="true"
              data-testid="composer-error"
            >
              {errorMessage ?? ''}
            </span>

            {/* Async status (errors, unavailable, success) — always mounted so assistive */}
            {/* technology registers the live region before content arrives. */}
            {/* role="status" implies aria-live="polite". */}
            <span
              id="composer-status"
              className="composer-status"
              role="status"
              aria-atomic="true"
              data-testid="composer-status"
            >
              {hasError ? error : ''}
            </span>
          </div>

          {/* Error retry control — only rendered when an error is present */}
          {hasError && (
            <div className="composer-error-actions">
              <button
                type="button"
                className="composer-retry-btn"
                onClick={onRetry}
                data-testid="composer-retry"
              >
                Попробовать ещё раз
              </button>
            </div>
          )}

          {/* Publish button — node 38:2396 */}
          <div className="composer-publish-row">
            {/*
             * type="submit" so clicking or pressing Enter in the form invokes
             * the form's onSubmit, which calls onPublish().
             * The button has no direct onClick — submit is routed through the form.
             */}
            <button
              type="submit"
              className="composer-publish"
              disabled={submitDisabled}
              aria-disabled={submitDisabled}
              aria-label={isSubmitting ? 'Публикация...' : PUBLISH_LABEL}
              data-testid="composer-publish"
            >
              {isSubmitting ? 'Публикация...' : PUBLISH_LABEL}
            </button>
          </div>
        </div>
      </footer>
    </form>
  );
}

/**
 * Map a validation error code to a human-readable Russian message.
 */
function validationMessageForError(error: string): string {
  switch (error) {
    case 'content_empty':
      return 'Введите текст записи';
    case 'content_whitespace':
      return 'Текст записи не может быть пустым';
    case 'content_too_long':
      return `Текст превышает ${POST_MAX_LENGTH} символов`;
    default:
      return '';
  }
}
